/**
 * The "add a sender ID" input: SMSWiser's rule checked as you type (1–11
 * letters or digits), a duplicate refused whatever its case, the count
 * beside it, Enter or the button adds.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { AlertCircle, Plus } from 'lucide-react';

import { DROP_GEN_SENDER_ID_MAX, DROP_GEN_SENDERS_PER_DROP_MAX, senderIdProblem } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

interface Props {
  existing: string[];
  onAdd: (name: string) => void;
}

function problemOf(name: string, existing: string[]): string | null {
  if (name.length === 0) return null;
  if (existing.some((x) => x.toUpperCase() === name.toUpperCase())) return 'Already in the list';
  if (existing.length >= DROP_GEN_SENDERS_PER_DROP_MAX) return `At most ${DROP_GEN_SENDERS_PER_DROP_MAX} sender IDs on one gateway row — SMSWiser's cap per drop`;
  return senderIdProblem(name);
}

export const SenderIdInput: FC<Props> = ({ existing, onAdd }) => {
  const [value, setValue] = useState('');
  const name = value.trim();
  const problem = problemOf(name, existing);
  const canAdd = name.length > 0 && problem === null;
  const add = () => {
    if (!canAdd) return;
    onAdd(name);
    setValue('');
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex w-full items-center gap-2">
        <label className={cn('flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border bg-background px-2 text-xs', problem ? 'border-destructive ring-2 ring-destructive/15' : 'border-input focus-within:border-primary')}>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            placeholder={`New sender ID — up to ${DROP_GEN_SENDER_ID_MAX} letters or digits`}
            aria-label="New sender ID"
            maxLength={DROP_GEN_SENDER_ID_MAX + 5}
            className="min-w-0 flex-1 bg-transparent font-mono tracking-wide outline-none placeholder:font-sans placeholder:tracking-normal"
          />
          <span className={cn('shrink-0 text-[10px] tabular-nums', name.length > DROP_GEN_SENDER_ID_MAX ? 'text-destructive' : 'text-muted-foreground')}>
            {name.length} / {DROP_GEN_SENDER_ID_MAX}
          </span>
        </label>
        <button type="button" onClick={add} disabled={!canAdd} className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md border border-border/70 px-2.5 text-xs font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50">
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add
        </button>
      </div>
      {problem ? (
        <p className="flex items-center gap-1 text-[11px] text-destructive duration-200 animate-in fade-in-0 motion-reduce:animate-none" role="alert">
          <AlertCircle className="h-3 w-3" aria-hidden />
          {problem}
        </p>
      ) : null}
    </div>
  );
};
